// ---------------------------------------------------------------------------
// nerisId.ts — NERIS ID format validation and parsing
//
// Official 10-digit alphanumeric format (no hyphen):
//   Positions 1-2  : Entity Type prefix (2 letters)
//   Positions 3-4  : State FIPS code (2 digits)
//   Positions 5-7  : County FIPS code (3 digits)
//   Positions 8-10 : Unique serial (3 digits)
//
// Example: FD06037123
//   FD  = Fire Department
//   06  = California (state FIPS)
//   037 = Los Angeles County (county FIPS)
//   123 = Agency serial within that county
// ---------------------------------------------------------------------------

export const NERIS_ENTITY_TYPES = [
  { code: 'FD', label: 'Fire Department',        internal: false },
  { code: 'FM', label: 'Fire Marshal',            internal: false },
  { code: 'EM', label: 'Emergency Management',   internal: false },
  { code: 'PS', label: 'Public Safety / PSAP',   internal: false },
  { code: 'SL', label: 'Streamline Internal',    internal: true  },
] as const;

// SL IDs use FIPS 99999 (reserved) + 3-digit incrementing serial
// e.g. SL99999001, SL99999002, ...
export const SL_FIPS = '99999';

/**
 * Returns true if this NERIS ID is an internal Streamline demo/test ID.
 * SL-prefixed IDs are never verified externally and never shown in self-signup.
 */
export function isInternalNerisId(nerisId: string): boolean {
  return nerisId.trim().toUpperCase().startsWith('SL');
}

/**
 * Builds the next SL internal ID given the last used serial number.
 * Serial increments from 001 upward. FIPS is always 99999.
 */
export function buildNextSlId(lastSerial: number): string {
  const serial = String(lastSerial + 1).padStart(3, '0');
  return `SL${SL_FIPS}${serial}`;
}

export type NerisEntityType = typeof NERIS_ENTITY_TYPES[number]['code'];

// Full NERIS ID: 2 letters + 8 digits (5-digit FIPS + 3-digit serial) = 10 chars total
// SL prefix uses FIPS 99999 (reserved) + 3-digit serial
export const NERIS_REGEX = /^(FD|FM|EM|PS|SL)\d{8}$/;
export const NERIS_EXTERNAL_REGEX = /^(FD|FM|EM|PS)\d{8}$/;

// Just the 8-digit numeric portion (FIPS 5 + serial 3)
export const NERIS_NUMBER_REGEX = /^\d{8}$/;

/**
 * Returns true if the full NERIS ID (e.g. "FD06037123") is valid.
 */
export function validateNerisId(nerisId: string): boolean {
  return NERIS_REGEX.test(nerisId.trim().toUpperCase());
}

export interface NerisSegments {
  entityType: string;
  stateFips: string;
  countyFips: string;
  serial: string;
  number: string;
  full: string;
}

/**
 * Parses a NERIS ID into its components.
 * Returns null if the ID is not in a parseable format.
 */
export function parseNerisSegments(nerisId: string): NerisSegments | null {
  const trimmed = nerisId.trim().toUpperCase();
  if (!NERIS_REGEX.test(trimmed)) return null;
  const entityType = trimmed.slice(0, 2);
  const number = trimmed.slice(2);       // 8 digits
  const stateFips = trimmed.slice(2, 4);
  const countyFips = trimmed.slice(4, 7);
  const serial = trimmed.slice(7, 10);
  return { entityType, stateFips, countyFips, serial, number, full: trimmed };
}

/**
 * Builds a full NERIS ID from its parts.
 */
export function buildNerisId(entityType: string, number: string): string {
  const digits = number.replace(/\D/g, '').padStart(8, '0').slice(0, 8);
  return `${entityType.toUpperCase()}${digits}`;
}

/**
 * Returns the display label for a NERIS entity type code.
 */
export function getNerisEntityLabel(code: string): string {
  return NERIS_ENTITY_TYPES.find((t) => t.code === code)?.label ?? code;
}
